import { useEffect, useState } from "react";
import { Briefcase, Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getAllVacancies, createVacancy, updateVacancy, deleteVacancy } from "@/lib/api";

const typeOptions = [
  { value: "trainer", label: "Trainer" },
  { value: "volunteer", label: "Vrijwilliger" },
  { value: "staff", label: "Staff" }
];

const typeColors = {
  trainer: "bg-blue-500/20 text-blue-600 border-blue-500/30",
  volunteer: "bg-green-500/20 text-green-600 border-green-500/30",
  staff: "bg-purple-500/20 text-purple-600 border-purple-500/30"
};

const emptyForm = {
  title: "",
  description: "",
  type: "trainer",
  requirements: "",
  contact_email: "info@futsalgent.be",
  contact_phone: "",
  location: "Sporthal Gent",
  is_active: true,
  deadline: ""
};

export default function AdminVacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);

  const fetchVacancies = async () => {
    try {
      const res = await getAllVacancies();
      setVacancies(res.data);
    } catch (error) {
      console.error("Error fetching vacancies:", error);
      toast.error("Kon vacatures niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVacancies();
  }, []);

  const handleOpenCreate = () => {
    setFormData(emptyForm);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (vacancy) => {
    setFormData({
      title: vacancy.title,
      description: vacancy.description,
      type: vacancy.type,
      requirements: vacancy.requirements || "",
      contact_email: vacancy.contact_email,
      contact_phone: vacancy.contact_phone || "",
      location: vacancy.location,
      is_active: vacancy.is_active,
      deadline: vacancy.deadline || ""
    });
    setSelectedVacancy(vacancy);
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleOpenDelete = (vacancy) => {
    setSelectedVacancy(vacancy);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.description) {
      toast.error("Titel en beschrijving zijn verplicht");
      return;
    }

    try {
      if (isEditing) {
        await updateVacancy(selectedVacancy.id, formData);
        toast.success("Vacature bijgewerkt");
      } else {
        await createVacancy(formData);
        toast.success("Vacature aangemaakt");
      }
      setDialogOpen(false);
      fetchVacancies();
    } catch (error) {
      console.error("Error saving vacancy:", error);
      toast.error("Kon vacature niet opslaan");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteVacancy(selectedVacancy.id);
      toast.success("Vacature verwijderd");
      setDeleteDialogOpen(false);
      fetchVacancies();
    } catch (error) {
      console.error("Error deleting vacancy:", error);
      toast.error("Kon vacature niet verwijderen");
    }
  };

  const toggleActive = async (vacancy) => {
    try {
      await updateVacancy(vacancy.id, { is_active: !vacancy.is_active });
      toast.success(vacancy.is_active ? "Vacature gedeactiveerd" : "Vacature geactiveerd");
      fetchVacancies();
    } catch (error) {
      console.error("Error toggling vacancy:", error);
      toast.error("Kon status niet wijzigen");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="h-96 bg-slate-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-vacancies-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Briefcase className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Vacatures</h1>
            <p className="text-slate-600">Beheer vacatures voor trainers en vrijwilligers</p>
          </div>
        </div>
        <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Nieuwe Vacature
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Totaal</p>
            <p className="text-2xl font-bold text-slate-900">{vacancies.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Actief</p>
            <p className="text-2xl font-bold text-green-600">{vacancies.filter(v => v.is_active).length}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-6">
            <p className="text-sm text-slate-500">Inactief</p>
            <p className="text-2xl font-bold text-slate-400">{vacancies.filter(v => !v.is_active).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Vacancies Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200">
                <TableHead className="text-slate-600">Titel</TableHead>
                <TableHead className="text-slate-600">Type</TableHead>
                <TableHead className="text-slate-600">Locatie</TableHead>
                <TableHead className="text-slate-600">Deadline</TableHead>
                <TableHead className="text-slate-600">Status</TableHead>
                <TableHead className="text-slate-600">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vacancies.length > 0 ? (
                vacancies.map((vacancy) => (
                  <TableRow key={vacancy.id} className="border-slate-200 hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">{vacancy.title}</TableCell>
                    <TableCell>
                      <Badge className={typeColors[vacancy.type]}>
                        {typeOptions.find(t => t.value === vacancy.type)?.label || vacancy.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{vacancy.location}</TableCell>
                    <TableCell className="text-slate-600">{vacancy.deadline || "-"}</TableCell>
                    <TableCell>
                      <Badge className={vacancy.is_active ? "bg-green-500/20 text-green-600" : "bg-slate-200 text-slate-500"}>
                        {vacancy.is_active ? "Actief" : "Inactief"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => toggleActive(vacancy)}
                          className="border-slate-200"
                        >
                          {vacancy.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenEdit(vacancy)}
                          className="border-slate-200"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleOpenDelete(vacancy)}
                          className="border-slate-200 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    Geen vacatures gevonden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">
              {isEditing ? "Vacature Bewerken" : "Nieuwe Vacature"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Titel *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="bijv. Jeugdtrainer U15"
                  className="border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(value) => setFormData({...formData, type: value})}
                >
                  <SelectTrigger className="border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Beschrijving *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Beschrijf de functie..."
                rows={4}
                className="border-slate-200"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700">Vereisten</Label>
              <Textarea
                value={formData.requirements}
                onChange={(e) => setFormData({...formData, requirements: e.target.value})}
                placeholder="Beschrijf de vereisten (optioneel)..."
                rows={3}
                className="border-slate-200"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Contact Telefoon</Label>
                <Input
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  placeholder="+32 9 123 45 67"
                  className="border-slate-200"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Locatie</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Deadline</Label>
                <Input
                  value={formData.deadline}
                  onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                  placeholder="bijv. 31 december 2024"
                  className="border-slate-200"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
              />
              <Label className="text-slate-700">Actief (zichtbaar op website)</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">
              Annuleren
            </Button>
            <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isEditing ? "Opslaan" : "Aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Vacature Verwijderen</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Weet je zeker dat je de vacature "{selectedVacancy?.title}" wilt verwijderen? 
            Deze actie kan niet ongedaan gemaakt worden.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">
              Annuleren
            </Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Verwijderen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
